import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MouseIcon from "@mui/icons-material/Mouse";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { styled } from "@mui/material/styles";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

const fadeInOutAnimation = {
  "0%": {
    opacity: 0,
  },
  "50%": {
    opacity: 1,
  },
  "100%": {
    opacity: 0,
  },
};

const SidePanelContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  padding: "0px 40px",
  height: "70%",
  width: "100%",
  position: "relative",
  overflowY: "auto",

  scrollbarWidth: "revert-layer",
  "&::-webkit-scrollbar": {
    width: "8px", // Adjust as needed
    height: "8px", // Adjust as needed
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "gray",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
    },
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-corner": {
    display: "none",
  },
  "&::-webkit-scrollbar-button": {
    display: "none",
  },

  // Apply animation
  animation: `${fadeInOutAnimation} 1.5s infinite`,
});

const RightArrowIconButton = styled(ChevronRightIcon)({
  color: "white",
  position: "relative",
  alignSelf: "flex-end",
  marginTop: "70px",
  marginBottom: "15px",
  top: "50px",
  right: "470px",
  cursor: "pointer",
  zIndex: 1000,
  backgroundColor: "gray",
  fontSize: "2rem",
  transition: "transform 0.3s",
  "&:hover": {
    backgroundColor: "rgba(169, 169, 169, 0.7)",
    transform: "scale(1.1, 1.1)",
  },
});

const SidePanel = ({ isOpen, onClose, ssuidDetail }) => {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const [pdfUrls, setPdfUrls] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentPdfIndex(0);
  }, [isOpen]);

  useEffect(() => {
    const fetchPdfs = async () => {
      if (!ssuidDetail || ssuidDetail.length === 0) return;

      setLoading(true);
      try {
        const fetchedUrls = await Promise.all(
          ssuidDetail.map(async (url) => {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Failed to fetch: ${url}`);
            const blob = await res.blob();
            return URL.createObjectURL(blob);
          })
        );
        setPdfUrls(fetchedUrls);
      } catch (err) {
        console.error("Error fetching PDFs", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPdfs();
  }, [ssuidDetail]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1); // Reset to first page of new document
  };

  useEffect(() => {
    setNumPages(null); // Reset to wait for new document
    setPageNumber(1); // Reset to first page
  }, [currentPdfIndex]);

  const goToPrevPdf = () => {
    if (currentPdfIndex > 0) {
      setCurrentPdfIndex((prev) => prev - 1);
    }
  };

  const goToNextPdf = () => {
    if (currentPdfIndex < ssuidDetail.length - 1) {
      setCurrentPdfIndex((prev) => prev + 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) setPageNumber((prev) => prev + 1);
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) setPageNumber((prev) => prev - 1);
  };

  return (
    <>
      <Drawer anchor="right" open={isOpen}>
        <div className="empty-div"></div>
        <Divider />
        <RightArrowIconButton onClick={onClose} />

        <SidePanelContainer style={{ width: 500 }}>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {loading || pdfUrls.length === 0 ? (
              <div
                style={{
                  width: 400,
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                Loading PDFs...
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "20px" }}>
                  <strong>
                    PDF {currentPdfIndex + 1} of {ssuidDetail.length}
                  </strong>
                </div>

                <Document
                  file={pdfUrls[currentPdfIndex]}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div>Loading PDF...</div>}
                  style={{ height: "50vh" }}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={400}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              </>
            )}
          </div>
        </SidePanelContainer>
        <div
          style={{
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ marginTop: 10 }}>
            Page {pageNumber} of {numPages}
          </div>

          <div style={{ display: "flex", gap: "10px", marginTop: 10 }}>
            <Button onClick={goToPrevPage} disabled={pageNumber === 1}>
              Previous Page
            </Button>
            <Button onClick={goToNextPage} disabled={pageNumber === numPages}>
              Next Page
            </Button>
          </div>

          {/* PDF Navigation Buttons */}
          <div style={{ display: "flex", marginTop: 20, gap: "10px" }}>
            <Button
              variant="contained"
              onClick={goToPrevPdf}
              disabled={currentPdfIndex === 0}
            >
              Previous PDF
            </Button>
            <Button
              variant="contained"
              onClick={goToNextPdf}
              disabled={currentPdfIndex === ssuidDetail.length - 1}
            >
              Next PDF
            </Button>
          </div>
        </div>
        {/* 
        <StickyDiv className="ml-3 flex">
          <ScrollText>Scroll Down</ScrollText>
          <MouseIconWrapper />
        </StickyDiv> */}
      </Drawer>
    </>
  );
};

export default SidePanel;
